// The web app's Sentry client. The entry imports this statically and initialises it before either boot
// target loads anything lazily, so a failure while the boot chunks load still reports with the
// release and environment attached.
import * as Sentry from '@sentry/browser'
import { withNoResponseRequest } from '../helpers/no-response-request'
import { withRejectionShape } from '../helpers/rejection-shape'
import { shouldReportEvent } from '../helpers/sentry-filter'

const TRACES_SAMPLE_RATE = 0.1
const SEGMENT_NAME_SOURCE = 'sentry.segment.name.source'

const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

const originOf = (baseUrl: string | undefined): string | undefined => {
  try {
    return baseUrl ? new URL(baseUrl).origin : undefined
  } catch {
    return undefined
  }
}

/** Anchored patterns for the backends' origins: requests to them, and to nothing else, carry `sentry-trace` and
 * `baggage`, so a page view's trace continues into the API, chat and MCP transactions it causes. */
export const tracePropagationTargets = (baseUrls: readonly (string | undefined)[]): RegExp[] =>
  baseUrls.flatMap((baseUrl) => {
    const origin = originOf(baseUrl)
    return origin ? [new RegExp(`^${escapeRegExp(origin)}(/|$)`)] : []
  })

/** Names a page after the route pattern it matched, since its URL carries ids that would split one page into many. */
export const routeName = (matchedPatterns: readonly string[], pathname: string): string =>
  matchedPatterns.at(-1) || pathname

/** Starts the navigation span for a route change; a query-string rewrite is not one, so it never reaches here. */
export const traceRouteChange = (route: string): void => {
  const client = Sentry.getClient()
  if (!client) return
  Sentry.startBrowserTracingNavigationSpan(client, { name: route, attributes: { [SEGMENT_NAME_SOURCE]: 'route' } })
}

/** Renames a still-running page-load span after the route the app booted onto. */
export const nameInitialRoute = (route: string): void => {
  const active = Sentry.getActiveSpan()
  if (!active) return
  const root = Sentry.getRootSpan(active)
  const json = Sentry.spanToJSON(root)
  if (!('op' in json) || json.op !== 'pageload') return
  root.updateName(route)
  root.setAttribute(SEGMENT_NAME_SOURCE, 'route')
}

export const initSentry = (): void => {
  Sentry.init({
    dsn: 'https://379a086aa0de3124767ba3bf710032f5@o4507035087273984.ingest.us.sentry.io/4507302023987200',
    release: import.meta.env.VITE_RELEASE_TAG,
    environment: import.meta.env.MODE,
    integrations: [Sentry.browserTracingIntegration({ instrumentNavigation: false }), Sentry.replayIntegration()],
    tracesSampleRate: TRACES_SAMPLE_RATE,
    tracePropagationTargets: tracePropagationTargets([
      import.meta.env.VITE_API_URL,
      import.meta.env.VITE_CHAT_API_URL,
      import.meta.env.VITE_MCP_BASE_URL
    ]),
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    enabled: !import.meta.env.DEV,
    dataCollection: { userInfo: false, cookies: false, httpHeaders: { allow: ['user-agent'] }, httpBodies: [] },
    ignoreErrors: [
      'Called on script loaded before session recording is available',
      'bufferBelongsToIframe',
      'Blocked a frame with origin',
      'Invalid call to runtime.sendMessage',
      'Could not establish connection. Receiving end does not exist',
      'Not implemented on this platform',
      '_AutofillCallbackHandler',
      'Java object is gone',
      'data-jam-capture',
      'ResizeObserver loop completed with undelivered notifications'
    ],
    denyUrls: [
      /profitwell\.js/,
      /public\.profitwell\.com/,
      /beacon\.min\.js/,
      /frame-modern\..*\.js/,
      /app~tooltips-modern/,
      /^chrome-extension:\/\//,
      /^moz-extension:\/\//,
      /^safari-(web-)?extension:\/\//,
      /^iabjs:\/\//,
      /\.safariextension\//,
      /js\.jam\.dev\//
    ],
    beforeSend(event, hint) {
      if (!shouldReportEvent(event, window.location.origin)) return null
      const shaped = withRejectionShape(event, hint.originalException, window.location.pathname)
      return withNoResponseRequest(shaped, hint.originalException, navigator.onLine)
    }
  })
}
